import { useParams } from 'react-router-dom';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useTranslation } from 'react-i18next';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useProcessorById } from '@/lib/api/processors';
import { Toaster } from '@/components/ui/toaster';
import { StripeProcessorDetailsPage } from './stripe/page';
import { NMIProcessorDetailsPage } from './nmi/page';
import { OnyxProcessorDetailsPage } from './onyx/page';
import { RouteSenseProcessorDetailsPage } from './routesense/page';
import { FinixProcessorDetailsPage } from './finix/page';
import { MastercardProcessorDetailsPage } from './mastercard/page';
import { AirwallexProcessorDetailsPage } from './airwallex/page';
import { CheckoutProcessorDetailsPage } from './checkout/page';
import { TagadaPayProcessorDetailsPage } from './tagadapay/page';
import { TagadaPayRouterDetailsPage } from './tagadapay-router/page';
import { EfiPaymentsProcessorDetailsPage } from './efipayments/page';
import { MerchantScaleProcessorDetailsPage } from './merchantscale/page';
import { BakolaPayProcessorDetailsPage } from './bakolapay/page';
import { SandboxDetailsPage } from './sandbox/page';
import { TrustFlowProcessorDetailsPage } from './trustflow/page';
import { KessPayProcessorDetailsPage } from './kesspay/page';
import { OvriProcessorDetailsPage } from './ovri/page';
import { CCAvenueProcessorDetailsPage } from './ccavenue/page';
import { NgeniusProcessorDetailsPage } from './ngenius/page';
import { TapProcessorDetailsPage } from './tap/page';
import { AuthorizeNetProcessorDetailsPage } from './authorizenet/page';
import { ConvesioProcessorDetailsPage } from './convesio/page';
import { AdyenProcessorDetailsPage } from './adyen/page';
import { GenericProcessorDetailsPage } from './generic/page';
import { PaylandProcessorDetailsPage } from './payland/page';
import { PaysafeProcessorDetailsPage } from './paysafe/page';
import { BlueSnapProcessorDetailsPage } from './bluesnap/page';
import { WorldpayProcessorDetailsPage } from './worldpay/page';
import { BraintreeProcessorDetailsPage } from './braintree/page';
import { MollieProcessorDetailsPage } from './mollie/page';
import { FiservProcessorDetailsPage } from './fiserv/page';
import { PaystackProcessorDetailsPage } from './paystack/page';
import { PayUProcessorDetailsPage } from './payu/page';
import { RazorpayProcessorDetailsPage } from './razorpay/page';
import { PayOneProcessorDetailsPage } from './payone/page';
import { GlobalPayProcessorDetailsPage } from './globalpay/page';
import { BuziPayProcessorDetailsPage } from './buzipay/page';
import { NetValveProcessorDetailsPage } from './netvalve/page';
import { XoalaProcessorDetailsPage } from './xoala/page';
import { UnlimitProcessorDetailsPage } from './unlimit/page';
import { StripeProcessorDetailsSkeleton } from './components/processor-details-skeleton';

const ProcessorDetailPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const { id } = useParams();
  const { data, isLoading, error } = useProcessorById(id!);

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <StripeProcessorDetailsSkeleton />
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  const renderProcessorDetails = () => {
    switch (data.type) {
      case 'stripe':
        return <StripeProcessorDetailsPage processor={data} />;
      case 'nmi':
        return <NMIProcessorDetailsPage processor={data} />;
      case 'onyx':
        return <OnyxProcessorDetailsPage processor={data} />;
      case 'routesense':
        return <RouteSenseProcessorDetailsPage processor={data} />;
      case 'finix':
        return <FinixProcessorDetailsPage processor={data} />;
      case 'mastercard':
        return <MastercardProcessorDetailsPage processor={data} />;
      case 'airwallex':
        return <AirwallexProcessorDetailsPage processor={data} />;
      case 'checkout':
        return <CheckoutProcessorDetailsPage processor={data} />;
      case 'tagadapay':
        return <TagadaPayProcessorDetailsPage processor={data} />;
      case 'tagadapay-router':
        return <TagadaPayRouterDetailsPage processor={data} />;
      case 'efipayments':
        return <EfiPaymentsProcessorDetailsPage processor={data} />;
      case 'merchantscale':
        return <MerchantScaleProcessorDetailsPage processor={data} />;
      case 'bakolapay':
        return <BakolaPayProcessorDetailsPage processor={data} />;
      case 'sandbox':
        return <SandboxDetailsPage processor={data} />;
      case 'trustflow':
        return <TrustFlowProcessorDetailsPage processor={data} />;
      case 'kesspay':
        return <KessPayProcessorDetailsPage processor={data} />;
      case 'ovri':
        return <OvriProcessorDetailsPage processor={data} />;
      case 'ccavenue':
        return <CCAvenueProcessorDetailsPage processor={data} />;
      case 'ngenius':
        return <NgeniusProcessorDetailsPage processor={data} />;
      case 'tap':
        return <TapProcessorDetailsPage processor={data} />;
      case 'authorizenet':
        return <AuthorizeNetProcessorDetailsPage processor={data} />;
      case 'convesio':
        return <ConvesioProcessorDetailsPage processor={data} />;
      case 'adyen':
        return <AdyenProcessorDetailsPage processor={data} />;
      case 'payland':
        return <PaylandProcessorDetailsPage processor={data} />;
      case 'paysafe':
        return <PaysafeProcessorDetailsPage processor={data} />;
      case 'bluesnap':
        return <BlueSnapProcessorDetailsPage processor={data} />;
      case 'worldpay':
        return <WorldpayProcessorDetailsPage processor={data} />;
      case 'braintree':
        return <BraintreeProcessorDetailsPage processor={data} />;
      case 'mollie':
        return <MollieProcessorDetailsPage processor={data} />;
      case 'fiserv':
        return <FiservProcessorDetailsPage processor={data} />;
      case 'paystack':
        return <PaystackProcessorDetailsPage processor={data} />;
      case 'payu':
        return <PayUProcessorDetailsPage processor={data} />;
      case 'razorpay':
        return <RazorpayProcessorDetailsPage processor={data} />;
      case 'payone':
        return <PayOneProcessorDetailsPage processor={data} />;
      case 'globalpay':
        return <GlobalPayProcessorDetailsPage processor={data} />;
      case 'buzipay':
        return <BuziPayProcessorDetailsPage processor={data} />;
      case 'netvalve':
        return <NetValveProcessorDetailsPage processor={data} />;
      case 'xoala':
        return <XoalaProcessorDetailsPage processor={data} />;
      case 'unlimit':
        return <UnlimitProcessorDetailsPage processor={data} />;
      default:
        return <GenericProcessorDetailsPage processor={data} />;
    }
  };

  return (
    <div className="container mx-auto">
      <Toaster />
      {renderProcessorDetails()}
    </div>
  );
};

export const ProcessorDetailPageContainer = withRequiredPermission(ProcessorDetailPageContainerInner, PERMISSIONS.PROCESSORS_VIEW);
